import {
  Add,
  CommentOutlined,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { Badge, Box, Button, Typography } from "@mui/material";
import React, { useEffect } from "react";
import NoteList from "./NoteList";
import FormDrawer from "components/Shared/FormDrawer";
import BasicNoteForm from "./BasicNoteForm";
import { toast } from "react-toastify";
import {
  useAddNoteSourceMutation,
  useGetNotesBySourceQuery,
  useUpdateNoteByIdMutation,
} from "store/services/noteApi";
import { getSocketInstance } from "socket/socket.io";
import { useAuth } from "hooks";

const CommentTab = ({ source, sourceRef, isActive }) => {
  const { loggedInUser } = useAuth();
  const [selectedComment, setSelectedComment] = React.useState(null);
  const [showReplies, setShowReplies] = React.useState(true);

  const { data, refetch } = useGetNotesBySourceQuery({
    source,
    sourceRef,
    isActive,
  });

  // Socket listener for real-time note updates
  useEffect(() => {
    const socket = getSocketInstance();
    if (!socket || !loggedInUser?._id) return;

    const eventName = `notifications/${loggedInUser._id}`;

    const handler = (notification) => {
      // Check if the notification is for a note on this shipment
      if (
        notification.sourceModel === "Shipment" &&
        notification.contextData?.shipmentId === sourceRef
      ) {
        refetch();
      }
    };

    socket.on(eventName, handler);
    return () => socket.off(eventName, handler);
  }, [loggedInUser?._id, sourceRef, refetch]);


  const [addCommentSource, { isLoading: addingComment }] =
    useAddNoteSourceMutation();
  const [updateCommentById, { isLoading: updatingComment }] =
    useUpdateNoteByIdMutation();

  const handleAddComment = async (data) => {
    try {
      await addCommentSource({ ...data, source, sourceRef }).unwrap();
      toast.success("Comment successfully added!");
      setSelectedComment(null);
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add comment!");
    }
  };

  const handleEditComment = (comment) => {
    setSelectedComment(comment);
  };

  const handleUpdateComment = async (data) => {
    const body = {
      ...data,
      noteId: data?._id,
    };
    try {
      await updateCommentById({ ...body }).unwrap();
      toast.success("Comment successfully updated!");
      setSelectedComment(null);
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update comment!");
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="end"
        justifyContent="space-between"
        padding={3}
      >
        <Box display="inline-flex" alignItems="center" gap={1.5}>
          <Badge color="primary">
            <CommentOutlined style={{ color: "gray" }} />
          </Badge>
          <Typography style={{ fontWeight: 500 }}>Notes</Typography>
        </Box>
        <Box>
          <Button
            startIcon={<Add />}
            variant="outlined"
            disabled={isActive === false}
            onClick={() => setSelectedComment(true)}
          >
            Note
          </Button>
          <Button
            sx={{ marginLeft: "20px" }}
            startIcon={showReplies ? <ExpandLess /> : <ExpandMore />}
            variant="outlined"
            disabled={isActive === false}
            onClick={() => setShowReplies((prev) => !prev)}
          >
            {showReplies ? "Hide Replies" : "Show Replies"}
          </Button>
        </Box>
      </Box>
      <NoteList
        notes={data}
        onCommentEdit={handleEditComment}
        showReplies={showReplies}
        isActive = {isActive}
      />
      <FormDrawer
        title="Note"
        open={!!selectedComment}
        onClose={() => setSelectedComment(null)}
      >
        <BasicNoteForm
          isBusy={updatingComment || addingComment}
          noteDetail={selectedComment === true ? null : selectedComment}
          onSubmit={(data) =>
            selectedComment && selectedComment !== true
              ? handleUpdateComment(data)
              : handleAddComment(data)
          }
          onCancel={() => setSelectedComment(null)}
          isShowClient={false}
          source={source}
        />
      </FormDrawer>
    </Box>
  );
};

export default CommentTab;